import { html, render } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { Dialog } from '@vaadin/vaadin-core'
import { View } from '../view'
import { DeviceStore, DownloadResult } from '../../stores'

const closeIcon = html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>`
const alertIcon = html`<svg class="ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>`

@customElement('kob-device-wired-backup')
export class DeviceWiredBackup extends View {

    @property({ type: Object })
    deviceStore: DeviceStore

    @property()
    dialogOpened: boolean

    result: DownloadResult

    formRoot

    render() {
        return html`
            <vaadin-dialog
                aria-label="Backup device"
                overlay-class="kob-modal"
                .opened="${this.dialogOpened}"
                @opened-changed="${(e: CustomEvent) => (this.dialogOpened = e.detail.value)}"
                .renderer="${this.renderContent}"
            ></vaadin-dialog>
        `
    }

    handleClose = () => {
        this.deviceStore = null
        this.dialogOpened = false
    }

    handleBackup = async () => {
        this.result = null
        this.renderContent(this.formRoot)

        this.autorun(() => {
            this.renderContent(this.formRoot)
        })

        const result = await this.deviceStore.backup()
        if (result.status !== 'error' && result.status !== 'cancel') {
            this.dialogOpened = false
            return
        }

        this.result = result
        this.renderContent(this.formRoot)
    }

    header = (title) => html`
        <div class="modal-head">
            <h3 class="modal-title">${title}</h3>
            <button class="modal-close" aria-label="Close" @click="${this.handleClose}">${closeIcon}</button>
        </div>
    `

    renderSuccess = (root: HTMLElement, dialog?: Dialog) => {
        render(html`
            <div class="kob-dialog">
                ${this.header('All done')}
                <div class="modal-body">
                    <p>Your reading progress and highlights are saved. You can unplug the Kobo.</p>
                </div>
                <div class="modal-foot right">
                    <button class="btn btn-primary btn-sm" @click="${this.handleClose}">Done</button>
                </div>
            </div>
        `, root)
    }

    isMobile = () => /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)

    mobileTemplate = () => html`
        <div class="kob-dialog">
            ${this.header(`Back up ${this.deviceStore.name}`)}
            <div class="modal-body">
                <div class="modal-banner danger">
                    ${alertIcon}
                    <div>A wired backup needs a desktop computer — your Kobo plugs into it with a cable.</div>
                </div>
            </div>
            <div class="modal-foot right">
                <button class="btn btn-ghost btn-sm" @click="${this.handleClose}">Cancel</button>
            </div>
        </div>
    `

    noDirectInstallTemplate = () => html`
        <div class="kob-dialog">
            ${this.header(`Back up ${this.deviceStore.name}`)}
            <div class="modal-body">
                <div class="modal-banner danger">
                    ${alertIcon}
                    <div>Wired backup works in desktop Chrome, Edge or Opera only.</div>
                </div>
            </div>
            <div class="modal-foot right">
                <button class="btn btn-ghost btn-sm" @click="${this.handleClose}">Close</button>
            </div>
        </div>
    `

    renderContent = (root: HTMLElement, dialog?: Dialog) => {
        this.formRoot = root

        const template = this.isMobile() ?
            this.mobileTemplate() :
            !this.deviceStore.canDirectInstall ?
            this.noDirectInstallTemplate() :
            html`
                <div class="kob-dialog">
                    ${this.header(`Back up ${this.deviceStore.name}`)}
                    <div class="modal-body">
                        ${this.result ?
                            html`
                                <div class="modal-banner danger">
                                    ${alertIcon}
                                    <div>${this.result.message}</div>
                                </div>
                            ` : ''
                        }
                        ${this.deviceStore.backupHappening
                            ? html`
                                <div>
                                    <div class="progress-label">${this.deviceStore.backupProgressMessage}</div>
                                    <vaadin-progress-bar value=${this.deviceStore.backupProgress}></vaadin-progress-bar>
                                </div>
                            `
                            : html`
                                <p>Make sure your Kobo is plugged in to this computer.</p>
                                <p>Select your Kobo and your reading progress and highlights will be backed up.</p>
                            `
                        }
                    </div>
                    ${this.deviceStore.backupHappening
                        ? html`
                            <div class="modal-foot right">
                                <button class="btn btn-ghost btn-sm" disabled>Working…</button>
                            </div>
                        `
                        : html`
                            <div class="modal-foot right">
                                <div class="right-group">
                                    <button class="btn btn-ghost btn-sm" @click="${this.handleClose}">Close</button>
                                    <button class="btn btn-primary btn-sm" @click="${this.handleBackup}">Select Kobo</button>
                                </div>
                            </div>
                        `
                    }
                </div>
            `

        render(template, root)
    }
}
